import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';

import { setModalIsOpened, useModalReducer } from '@/main/store/modal';
import { ModalEnum } from '@/main/services/enum/ModalEnum';
import * as S from './styles';

interface IModalSide {
  open: boolean;
  shouldRenderOpen?: boolean;
  children: React.ReactNode;
  id?: string;
  titleModalReducer?: string;
  title?: string | React.JSX.Element | undefined;
  padding?: string;
  width?: string;
  withStopPropagation?: boolean;
  closeModal: () => void;
  isClosedOtherModal?: boolean;
}
/**
 * Component to render Modal Side
 *
 * @param {boolean} open - Modal Side is open.
 * @param {boolean} shouldRenderOpen - When true, render children when open is true (only render first time).
 * @param {React.ReactNode} children - Children to render inside Modal Side
 * @param {string} titleModalReducer - Title that must appear in the reducer
 * @param {string} id - Id of Modal Side
 * @param {string} title - Title of Modal Side
 * @param {string} padding - Padding of Modal Side
 * @param {Function} closeModal - Function to close Modal Side.
 * @example
 * const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
 *
 * <KogModalSideComponent
 *  open={isOpenModal}
 *  closeModal={() => setIsOpenModal(false)}
 * >
 *  <div>Content</div>
 * </KogModalSideComponent>
 *
 */
function KogModalSideComponent({
  open,
  id,
  children,
  padding,
  width,
  title,
  titleModalReducer = ModalEnum.Default,
  shouldRenderOpen = false,
  withStopPropagation = false,
  closeModal,
  isClosedOtherModal = true
}: IModalSide) {
  const dispatch = useDispatch();
  const modalReducer = useModalReducer();
  const [renderChildren, setRenderChildren] = React.useState(!shouldRenderOpen);


  function handleCloseModal(e: React.MouseEvent<HTMLDivElement>) {
    if (withStopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    closeModal();
  }

  function handleClickInsideModal(e: React.MouseEvent<HTMLDivElement>) {
    if (withStopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  useEffect(() => {
    if (open && !renderChildren) {
      setRenderChildren(true);
    }
    dispatch(setModalIsOpened(open, open ? titleModalReducer : null));
  }, [open]);

  // Notation: This useEffect is used to close the modal when another modal is opened
  // Notation: Only when this modal side is open, otherwise an already closed modal side would
  // run its closeModal again and re-trigger side effects (e.g. confirmation before closing)
  useEffect(() => {
    const isOpenedOtherModal = isClosedOtherModal && modalReducer.open && modalReducer.name !== titleModalReducer;

    if (open && isOpenedOtherModal) {
      closeModal();
    }

  }, [JSON.stringify(modalReducer)])

  const Z_INDEX_MODAL = modalReducer.name === titleModalReducer ? 100 : 99;
  const Z_INDEX_MODAL_OVERLAY = modalReducer.name === titleModalReducer ? 50 : 49;
  return (
    <>
      {createPortal(
        <S.ModalSide id='id-general-modalSide'>
          <S.ModalSideContent
            padding={padding}
            width={width}
            className={`${open && 'show'}`}
            id={id}
            onClick={handleClickInsideModal}
            $zIndex={Z_INDEX_MODAL}
          >
            {title && <S.TitleModal>{title}</S.TitleModal>}
            {renderChildren && children}
          </S.ModalSideContent>
          {open && <S.ModalSideOverlay id='id-modal-sideOverlay' $zIndex={Z_INDEX_MODAL_OVERLAY} onClick={handleCloseModal} />}
        </S.ModalSide>,
        document.body
      )}
    </>
  );
}

export default KogModalSideComponent;
